import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import moment from "moment";
import { FormattedMessage } from "react-intl";

import useDecodedParams from "Hooks/useDecodedParams";
import { capitalize } from "Libs/utils";
import { getAutoscalingConfigurationThunk } from "Reducers/environment/service/service.slice";
import ActivityLogModal from "src/organization/pages/project/environment/metrics/components/ActivityLogModal/ActivityLogModal";
import {
  EmptyMetrics,
  GenericError
} from "src/organization/pages/project/environment/metrics/components/Errors";
import TimeframeControl from "src/organization/pages/project/environment/metrics/components/timeframeControl/TimeframeControl";
import Service from "src/organization/pages/project/environment/metrics/containers/Service";
import { useAutoscallingValues } from "src/organization/pages/project/environment/metrics/containers/Service/util";
import useInitialTimeframe from "src/organization/pages/project/environment/metrics/hooks/useInitialTimeframe";
import usePinnedTabsSync from "src/organization/pages/project/environment/metrics/hooks/usePinnedTabsSync";
import usePollActivities from "src/organization/pages/project/environment/metrics/hooks/usePollActivities";
import useStickyTimecontrol from "src/organization/pages/project/environment/metrics/hooks/useStickyTimecontrol";
import { defaultPollTime } from "src/organization/pages/project/environment/metrics/resources/settings";
import * as V from "src/organization/pages/project/environment/metrics/resources/views.styles";
import { parseTimeDuration } from "src/organization/pages/project/environment/metrics/utils/parseDimensions/parseDimensions";
import usePollgroup, {
  PollgroupContext
} from "src/organization/pages/project/environment/metrics/utils/polling/usePollgroup";
import { useAppDispatch } from "Store/hooks";

import { reAttachPersistentDisk } from "../totalUsage/grid.utils";
import UsageView from "../totalUsage/Page";
import usePollGridResouces from "../totalUsage/usePollGridResouces";
import { ResourceType, TimeFrameType } from "../types";

import { isAutoscaleType } from "./../../components/Chart/utils";
import * as S from "./Grid.styles";
import GridSkeleton from "./GridSkeleton";
import InstanceSelector from "./InstanceSelector";
import { useStubService } from "./util";

import type { Activity } from "@packages/client";

const Grid = () => {
  const [maxZoom, setMaxZoom] = useState(false);
  const dispatch = useAppDispatch();
  const initialTimeframe = useInitialTimeframe();
  const [timeframe, setTimeframe] = useState(initialTimeframe);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(true);
  const [metrics, setMetrics] = useState<ResourceType[] | undefined>();
  const [deployment, setDeployment] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>();
  const [activities, setActivities] = useState<Activity[]>([]);
  const { projectId, environmentId } = useDecodedParams();

  const [selectedInstance, setSelectedInstance] = useState<
    Record<string, string[]>
  >({});
  const [error, setError] = useState<unknown>();

  const pollInterval = parseTimeDuration(
    initialTimeframe.poll || defaultPollTime[timeframe.id || "fallback"]
  );
  const { hasCapability: isAutoscalingCapabilityEnabled } =
    useAutoscallingValues();

  const filteredActivities =
    deployment && isAutoscalingCapabilityEnabled
      ? activities
      : deployment
        ? activities.filter(
            activity => activity.type === "environment.redeploy"
          )
        : isAutoscalingCapabilityEnabled
          ? activities.filter(activity =>
              isAutoscaleType(activity?.payload?.deployment_type)
            )
          : [];

  useEffect(() => {
    setIsLoading(true);
  }, [timeframe]);

  const { data, servicesInstance } = usePollGridResouces({
    timeframe,
    onChange: data => {
      setMetrics(data);
      setLastUpdated(new Date());
    },
    onLoadingChange: isLoading => {
      setIsUpdating(isLoading);
      !isLoading && setIsLoading(false);
    },
    onError: setError,
    interval: pollInterval.seconds
  });

  usePollActivities({
    enabled: deployment || isAutoscalingCapabilityEnabled,
    timeframe,
    onChange: setActivities,
    interval: pollInterval.seconds
  });

  const { pollgroup } = usePollgroup({
    timeframe,
    interval: pollInterval.seconds
  });

  const onTimeframeChange = useCallback((timeframe: TimeFrameType) => {
    setTimeframe(timeframe);
    setMaxZoom(false);
  }, []);

  const onBrush = useCallback(
    (from: Date | string, to: Date | string) => {
      if (maxZoom) {
        setTimeframe(timeframe);
        return;
      }
      const selectedRange = moment(to).diff(moment(from), "minutes");
      if (selectedRange < 3) {
        const newEndpoint = moment(from).add(3, "minutes").format();
        setMaxZoom(true);
        setTimeframe({ from, to: newEndpoint });
      } else {
        setTimeframe({ from, to });
      }
    },
    [maxZoom, setMaxZoom, timeframe]
  );
  const serviceData = useMemo(() => reAttachPersistentDisk(data), [data]);

  const webAppStart = serviceData?.services.findIndex(
    service => service.kind === "webapp"
  );
  const serviceStart = serviceData?.services.findIndex(
    service => service.kind === "service"
  );
  const workerStart = serviceData?.services.findIndex(
    service => service.kind === "worker"
  );
  const firstPerSection = [webAppStart, serviceStart, workerStart];
  const serviceStub = useStubService();
  const services = serviceData.services || serviceStub;

  useEffect(() => {
    dispatch(getAutoscalingConfigurationThunk({ projectId, environmentId }));
  }, [dispatch, projectId, environmentId]);

  const headerRef = useRef<HTMLDivElement>(null);
  useStickyTimecontrol({ ref: headerRef });
  const { onSyncChange, isSynced, canJoinSync } = usePinnedTabsSync({
    setTimeframe: onTimeframeChange,
    timeframe
  });

  const handleSyncChange = (isSynced: boolean) => {
    onSyncChange(isSynced, timeframe);
  };

  return (
    <PollgroupContext.Provider value={{ pollgroup }}>
      <ActivityLogModal />
      <V.Header ref={headerRef}>
        <V.Heading tag="h2">
          <FormattedMessage id="metrics.grid.heading" />
        </V.Heading>

        <TimeframeControl
          isUpdating={isUpdating}
          timeframe={{
            onChange: onTimeframeChange,
            timeframe
          }}
          pollInterval={pollInterval}
          lastUpdated={lastUpdated}
          deployment={{ enabled: deployment, onChange: setDeployment }}
          tabSync={{
            onSyncChange: handleSyncChange,
            isSynced,
            canJoinSync
          }}
        />
      </V.Header>

      <S.Layout>
        {!!services.length && (
          <UsageView
            timeframe={timeframe}
            activities={filteredActivities}
            setTimeFrame={onTimeframeChange}
          />
        )}
        {!metrics?.length && isLoading ? (
          <GridSkeleton />
        ) : services.length ? (
          services.map((service, i) => {
            const selectedInstances: string[] & { prefixId?: null } =
              selectedInstance[service.id] ||
              servicesInstance[service.id] ||
              [];
            selectedInstances.prefixId = null;

            const instanceFilter = (
              <InstanceSelector
                key={service.id}
                instances={servicesInstance[service.id]}
                selected={selectedInstances}
                onChange={v => {
                  setSelectedInstance(prev => ({ ...prev, [service.id]: v }));
                }}
              />
            );

            const sectionLabel =
              service.kind === "webapp"
                ? "Applications"
                : `${capitalize(service.kind)}s`;
            return (
              <Fragment key={service.id}>
                {firstPerSection.includes(i) && (
                  <S.SectionHeader>{sectionLabel}</S.SectionHeader>
                )}

                <Service
                  //@ts-expect-error mismatch type
                  service={service}
                  selectedTimeframe={timeframe}
                  onBrush={onBrush}
                  hosts={selectedInstances}
                  isLoading={isLoading}
                  kind="grid"
                  deployment={deployment}
                  activities={activities || []}
                  maxZoom={maxZoom}
                  actions={instanceFilter}
                />
              </Fragment>
            );
          })
        ) : error ? (
          <GenericError error={error} defaultHeader={false} />
        ) : (
          <EmptyMetrics />
        )}
      </S.Layout>
    </PollgroupContext.Provider>
  );
};

export default React.memo(Grid);
